import { attackOf, effectiveStat, powerMult } from '../matchup';
import { DEFENSE_SLOTS, OFFENSE_SLOTS } from '../playdef';
import type { Side } from '../subs';
import { effectiveness, physicalTypeMult } from '../typechart';
import type {
  Combatant,
  DefensePlayDef,
  DefenseSlot,
  Game,
  Move,
  OffensePlayDef,
  OffenseSlot,
  StatName,
} from '../types';
import { maxWinChance, opponentOf, sideWinChance, winChance, type BoardKind } from './board';
import { leanedSlots, slotKind } from './call';
import { noise, type AiContext } from './context';

/**
 * Spends (slice 7): PP budgeting across the game. A move goes only on a slot the play leans on
 * whose role battles (never the passer or the hand-off), never on a Pokémon fresh this snap (it
 * cannot spend) or at 0 HP, and only if its first battle is likely to flip:
 *
 *   flip  = win chance with the move - without it, against the Pokémon across the board
 *   spend when flip x leverage + noise >= the horizon's bar (coach.ai.spend)
 *
 * snap: leverage 1, bar minFlip. drive: leverage (3rd/4th down, red zone, last quarter, the try),
 * bar minFlip x saveFactor. game: drive's bar / pace (PP share left / regulation share left),
 * dropping to minFlip x burnFactor in the last burnSeconds and overtime.
 */

type AnyPlay = OffensePlayDef | DefensePlayDef;
type BattleStat = Exclude<StatName, 'hp'>;

/** The chance a spent move adds to `ours` winning its battle of `kind` against `theirs`. */
export function flipOf(
  move: Move,
  side: Side,
  kind: BoardKind,
  ours: Combatant,
  theirs: Combatant,
  ctx: AiContext,
  /** The win chance without the move, when the caller has it. */
  p0: number = sideWinChance(side, kind, ours, theirs, ctx.b),
): number {
  const b = ctx.b;
  const fx = move.effect;
  if (fx.kind === 'attack') {
    const a = attackOf(move);
    const stat: BattleStat = a.category === 'physical' ? 'atk' : 'spa';
    const guard: BattleStat = a.category === 'physical' ? 'def' : 'spd';
    const stab = ours.types.includes(a.type) ? b.moves.attack.stab : 1;
    const hit =
      effectiveStat(ours, stat, b) *
      powerMult(a.power, b) *
      stab *
      Math.max(b.types.physicalFloor, effectiveness(a.type, theirs.types));
    const held =
      effectiveStat(theirs, guard, b) *
      physicalTypeMult(theirs.types, ours.types, b.types.physicalFloor, b.types.battleExponent);
    const acc = Math.max(b.moves.attack.accuracyFloor, a.accuracy);
    return acc * winChance(hit, held, b) + (1 - acc) * p0 - p0;
  }
  const staged = (stat: BattleStat, n: number) => {
    const now = ours.stages[stat] ?? 0;
    const to = Math.min(b.stages.cap, now + n);
    return to === now ? ours : { ...ours, stages: { ...ours.stages, [stat]: to } };
  };
  if (fx.kind === 'burst') {
    if (kind !== 'coverage') return 0;
    return sideWinChance(side, kind, staged('spe', fx.speedStages), theirs, b) - p0;
  }
  if (fx.kind === 'stage' && fx.stages > 0) {
    const up = staged(fx.stat, fx.stages);
    if (up === ours) return 0;
    return (sideWinChance(side, kind, up, theirs, b) - p0) * b.coach.ai.spend.stageDriveFactor;
  }
  return 0;
}

/** How much this snap matters for a spend: 1 on the snap horizon, else coach.ai.spend.leverage. */
export function leverageOf(game: Readonly<Game>, twoPoint: boolean, ctx: AiContext): number {
  if (ctx.horizon === 'snap') return 1;
  const L = ctx.b.coach.ai.spend.leverage;
  let x = 1;
  if (game.down >= 3) x *= L.thirdDown;
  if (game.ballOn >= L.redZoneBallOn) x *= L.redZone;
  if (game.quarter >= ctx.b.football.quarters) x *= L.lastQuarter;
  if (twoPoint) x *= L.twoPoint;
  return x;
}

/** Regulation time left as a share of the whole; 0 in overtime. */
export function timeLeftShare(game: Readonly<Game>, ctx: AiContext): number {
  const F = ctx.b.football;
  if (game.quarter > F.quarters) return 0;
  const total = F.quarters * F.quarterSeconds;
  return ((F.quarters - game.quarter) * F.quarterSeconds + game.clock) / total;
}

/** The bar a move's flip x leverage must clear, by horizon (and, on `game`, by PP pace). */
export function spendBar(game: Readonly<Game>, move: Move, ppLeft: number, ctx: AiContext): number {
  const S = ctx.b.coach.ai.spend;
  if (ctx.horizon === 'snap') return S.minFlip;
  if (ctx.horizon === 'drive') return S.minFlip * S.saveFactor;
  const F = ctx.b.football;
  const burn =
    game.quarter > F.quarters || (game.quarter === F.quarters && game.clock <= S.burnSeconds);
  if (burn) return S.minFlip * S.burnFactor;
  const pace = Math.min(
    S.paceMax,
    Math.max(S.paceMin, ppLeft / Math.max(1, move.pp) / Math.max(1e-6, timeLeftShare(game, ctx))),
  );
  return (S.minFlip * S.saveFactor) / pace;
}

/**
 * The moves to spend for one side's call: every slot named, `null` where nothing is spent, so the
 * play's default script never fires unplanned. `ours` is the chart after this call's window
 * (fresh subs carry `enteredThisPlay`); `theirs` the other side as it stands.
 */
export function planSpends(
  play: AnyPlay,
  side: Side,
  ours: Record<string, Combatant>,
  theirs: Record<string, Combatant>,
  game: Readonly<Game>,
  twoPoint: boolean,
  ctx: AiContext,
): Record<string, string | null> {
  const b = ctx.b;
  const slots: readonly string[] = side === 'offense' ? OFFENSE_SLOTS : DEFENSE_SLOTS;
  const moves: Record<string, string | null> = Object.fromEntries(slots.map((s) => [s, null]));
  const leverage = leverageOf(game, twoPoint, ctx);
  for (const s of leanedSlots(play)) {
    const k = slotKind(play, s, b);
    if (!k?.battles) continue;
    const c = ours[s] as Combatant;
    if (c.hp <= 0 || (b.subs.noMoveOnFirstSnap && c.enteredThisPlay)) continue;
    const them = theirs[opponentOf(s as OffenseSlot | DefenseSlot)] as Combatant;
    const p0 = sideWinChance(side, k.kind, c, them, b);
    // Already sure to win but for the crit: no move can flip it.
    if (p0 >= maxWinChance(b)) continue;
    let best: { move: Move; flip: number } | undefined;
    for (const m of c.moves) {
      if ((c.ppLeft[m.id] ?? 0) <= 0) continue;
      const kind = m.effect.kind;
      if (kind !== 'attack' && kind !== 'burst' && kind !== 'stage') continue;
      const flip = flipOf(m, side, k.kind, c, them, ctx, p0);
      if (flip > 0 && (!best || flip > best.flip)) best = { move: m, flip };
    }
    if (!best) continue;
    const score = best.flip * leverage + noise(ctx, b.coach.ai.jitterScale.spend);
    if (score >= spendBar(game, best.move, c.ppLeft[best.move.id] ?? 0, ctx))
      moves[s] = best.move.id;
  }
  return moves;
}
